import { useBranding } from "../branding";

interface Props {
  onClose: () => void;
}

export function AboutPage({ onClose }: Props) {
  const { appName, companyName, aboutExampleQuestions } = useBranding();
  // rotação diária (mesma lógica da tela inicial): mostra até 5 variando por dia
  const aboutQuestions =
    aboutExampleQuestions.length <= 5
      ? aboutExampleQuestions
      : (() => {
          const off = new Date().getDate() % aboutExampleQuestions.length;
          return [...aboutExampleQuestions.slice(off), ...aboutExampleQuestions.slice(0, off)].slice(0, 5);
        })();
  return (
    <div className="about-page" role="dialog" aria-label={`Sobre o ${appName}`}>
      <header className="about-header">
        <h1>Sobre o {appName}</h1>
        <button className="about-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </header>

      <section className="about-section">
        <h2>Para que serve</h2>
        <p>
          O {appName} é o assistente de IA da {companyName} que conhece o SEU contexto: ele lê os
          repositórios conectados da organização - backend, frontend, schemas e legados - e responde
          perguntas em português, do nível de negócio ao detalhe técnico.
        </p>
        <p>
          A ideia é simples: <strong>quem cuida do produto não deveria precisar abrir o código pra
          entender o produto</strong>. Pergunte como você falaria com um colega da engenharia, e
          receba respostas claras, com referências ao que existe de verdade.
        </p>
      </section>

      <section className="about-section">
        <h2>O que o sistema faz</h2>
        <ul className="about-list">
          <li>
            <strong>Conversas com contexto real.</strong> Dois modos (negócio e técnico), streaming de
            resposta, histórico pesquisável com tags, fixados, atribuição a colegas e exportação/importação
            de backup.
          </li>
          <li>
            <strong>Repositórios conectados.</strong> GitHub, GitLab (inclusive self-hosted) e Azure DevOps,
            por OAuth ou token - vários repositórios por workspace, agrupados por projeto, com sincronização
            automática em intervalo configurável e acesso <strong>somente leitura</strong>.
          </li>
          <li>
            <strong>Documentação automática.</strong> A IA escreve e mantém a documentação por repositório,
            por grupo e do workspace inteiro - regenerada quando o código muda, com status ao vivo e
            download em markdown.
          </li>
          <li>
            <strong>Perguntas agendadas e modelos de prompt.</strong> Rotinas que rodam sozinhas (ex.:
            resumo semanal do que mudou) e templates reutilizáveis pro time.
          </li>
          <li>
            <strong>API pública.</strong> Integre o assistente aos seus sistemas via API com chave própria
            (documentação em /integracao) e webhooks de eventos.
          </li>
          <li>
            <strong>Compartilhamento.</strong> Conversas podem virar links públicos de leitura pra quem não
            tem conta.
          </li>
        </ul>
      </section>

      <section className="about-section">
        <h2>Pra administração da organização</h2>
        <ul className="about-list">
          <li>
            <strong>White-label completo.</strong> Nome, logo, cores, perguntas de exemplo e domínio
            próprios por organização - incluindo subdomínio self-service ou domínio do cliente com
            verificação de posse.
          </li>
          <li>
            <strong>Acesso e segurança.</strong> Login com Google/Microsoft/GitHub, SAML corporativo,
            2FA/TOTP, convites de equipe, aprovação de contas e isolamento total entre organizações.
          </li>
          <li>
            <strong>Governança e custos.</strong> Plano com assentos e orçamento mensal de IA, estatísticas
            detalhadas por usuário/workspace com exportação CSV, alertas de orçamento e retenção de dados
            configurável (LGPD).
          </li>
        </ul>
      </section>

      <section className="about-section">
        <h2>Como funciona</h2>
        <p>
          O código dos repositórios é clonado em área isolada e lido em modo somente-leitura - o
          assistente <strong>nunca</strong> altera código nem envia seus dados pra fora do que você
          perguntou. Tokens de acesso ficam cifrados e nunca aparecem na tela.
        </p>
        <p>
          Toda conversa fica salva na sua organização e pode ser retomada, compartilhada ou apagada a
          qualquer momento. Você também pode baixar todos os seus dados (ou pedir a exclusão definitiva)
          em "Meus dados".
        </p>
      </section>

      <section className="about-section">
        <h2>Boas perguntas pra começar</h2>
        <ul className="about-list">
          {aboutQuestions.map((q) => (
            <li key={q}>"{q}"</li>
          ))}
        </ul>
      </section>

      <footer className="about-footer">
        <button className="about-back" onClick={onClose}>
          Voltar à conversa
        </button>
      </footer>
    </div>
  );
}
